'use client';

import { ReactNode, useRef } from 'react';
import {
  AspectRatio,
  AspectRatioProps,
  Box,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useSize } from '@chakra-ui/react-use-size';
import QRCode from 'react-qr-code';

import { useNetwork } from 'context/network';
import { Paths } from 'utils/paths';

const defaultSize = {
  height: 255,
  width: 405,
};

interface GiftcardData {
  amount: ReactNode;
  networkLogoUrl?: string;
  qrCodeUrl?: string;
  shortId: string;
  id: string;
  seqNum: string;
}

interface Props {
  maxWidth?: AspectRatioProps['maxWidth'];
  data: GiftcardData;
}

export const Giftcard = (props: Props) => {
  const { maxWidth = 'full', data } = props;

  const { network } = useNetwork();

  const containerRef = useRef<HTMLDivElement>(null);

  const dimensions = useSize(containerRef) ?? defaultSize;

  const convertPercentToREM = (percent: number) => {
    const percentValue = (percent / 100).toFixed(2);

    const pixelValue = (Number(percentValue) * dimensions.height).toFixed(2);

    const remValue = (Number(pixelValue) / 16).toFixed(2);

    return `${remValue}rem`;
  };

  const {
    networkLogoUrl = network?.logoWideUrl,
    amount,
    qrCodeUrl = `${window.location.origin}${Paths.CHECKOUT}`,
    shortId,
    id,
    seqNum,
  } = data;

  return (
    <AspectRatio
      ref={containerRef}
      maxWidth={maxWidth}
      ratio={defaultSize.width / defaultSize.height}
    >
      <Box
        h="full"
        w="full"
        bg="white"
        rounded={convertPercentToREM(5.6)}
        shadow="dark-lg"
        p={convertPercentToREM(9.4)}
        userSelect="none"
      >
        <VStack align="flex-start" w="full" spacing={convertPercentToREM(2)}>
          <HStack align="baseline" justify="space-between" w="full">
            <Image
              src={networkLogoUrl}
              h={convertPercentToREM(22)}
              alt="Network Logo"
            />

            <Heading
              display="flex"
              alignItems="baseline"
              fontSize={convertPercentToREM(23)}
              fontWeight="bold"
            >
              {amount}
              <Text
                ml="0.25rem"
                fontSize={convertPercentToREM(10)}
                fontWeight="bold"
              >
                €
              </Text>
            </Heading>
          </HStack>

          <HStack align="flex-start" w="full">
            <AspectRatio ratio={1 / 1} w="33%" h="full">
              <Box filter="blur(1px)">
                {qrCodeUrl && (
                  <QRCode
                    value={qrCodeUrl}
                    bgColor="#fff"
                    fgColor="#000"
                    level="M"
                  />
                )}
              </Box>
            </AspectRatio>

            <VStack
              align="flex-start"
              w="full"
              spacing={convertPercentToREM(1.25)}
            >
              <HStack
                fontSize={convertPercentToREM(6.1)}
                spacing={convertPercentToREM(1.25)}
                fontFamily="Source Code Pro, serif"
                filter="blur(1px)"
              >
                <Text fontWeight="bold">{shortId}</Text>
                <Text textAlign="center">·</Text>
                <Text>{id}</Text>
              </HStack>

              <VStack w="full" spacing={convertPercentToREM(1.25)}>
                <Box w="full" h={convertPercentToREM(20.84)} bg="gray.50" />
              </VStack>

              <HStack
                w="full"
                justify="space-between"
                fontSize={convertPercentToREM(3.1)}
                filter="blur(1px)"
              >
                <Text>Gutscheininfos? QR-Code scannen!</Text>

                <Text fontFamily="Source Code Pro, serif">{seqNum}</Text>
              </HStack>
            </VStack>
          </HStack>

          <HStack>
            <Text
              fontSize={convertPercentToREM(3.1)}
              lineHeight={convertPercentToREM(3.75)}
              filter="blur(1px)"
            >
              Dieser Gutschein ist nur bei teilnehmenden Betrieben einlösbar,
              darf nicht in Bargeld ausgezahlt und kann nicht zum Kauf weiterer
              Gutscheine verwendet werden. Herausgeber: zmyle GmbH | Aufladungen
              sind 3 Jahre plus Ende des Aufladejahres gültig | Es gelten die
              aktuellen Gutscheindaten unter: networkdomain/cardinfo
            </Text>
          </HStack>
        </VStack>
      </Box>
    </AspectRatio>
  );
};
